import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { ContentsRouteParameter, CatalogFacet } from '@vestiaire/api-sdk-angular';
import { IBaseItem } from '@app/shared/components/catalog/catalog-widgets/catalog-widget';
import { LocaleService } from '@app/core/i18n';
import { CatalogFilter } from '@app/shared/model/interfaces/catalog';

export const ToggleFacets = ['sold.sold', 'stock', 'dsEligibleCountries'];

export const FACET_LINKS_MAPPING = {
  'CATALOG.FILTERS.UNIVERSE': 'universes',
  'CATALOG.FILTERS.BRANDS': 'brand',
  'CATALOG.FILTERS.CATEGORY': 'categories',
  'CATALOG.FILTERS.COLORS': 'color',
  'CATALOG.FILTERS.MATERIALS': 'material',
  'CATALOG.FILTERS.MODEL': 'model',
  'CATALOG.FILTERS.EDITORPICKS': 'editorPicks',
  'CATALOG.FILTERS.DISCOUNT': 'editorPicks'
};

export interface PriceRangeState {
  resetMin?: boolean;
  resetMax?: boolean;
}

/**
 * Parse toggle item label
 * @param label
 * @param facet
 */
export const parseToggleItemLabel = (label: string, facet: string): IBaseItem => {
  const item: IBaseItem = { label: `${facet}_${label === '1' ? 'on' : 'off'}` };
  return item;
};

/**
 * Return catalog link of filter item
 *
 * @param item
 * @param filterItemUrls
 */
export const getItemUrl = (item: IBaseItem, filterItemUrls?: CatalogFacet[]): string => {
  let result = null;
  if (filterItemUrls) {
    const itemId = item.label === 'Vintage' ? 'vintage' : String(item.id);
    const url = filterItemUrls.find(facet => facet.facetId === itemId);

    if (url) {
      result = url.path;
    }
  }

  return result;
};

/**
 * Return regular experssion template to use when aprsing filter value
 * @param isHierarchical
 */
export const getRegExpToUse = (isHierarchical: boolean): RegExp => {
  return isHierarchical ? /.*> (.+)#([0-9]+)$/i : /(.+)#([0-9]+)$/i;
};

/**
 * Explode & parse facets values
 * @param label
 * @param isHierarchical
 * @param facetName
 * @param filterItemUrls
 */
export const parseItemLabelAndId = (
  label: string,
  isHierarchical = false,
  facetName?: string,
  filterItemUrls?: CatalogFacet[]
): IBaseItem => {
  let result: IBaseItem;
  if (facetName) {
    result = parseToggleItemLabel(label, facetName);
  } else {
    const item: IBaseItem = { label };
    const regexp = getRegExpToUse(isHierarchical);
    const match = label.match(regexp);

    if (match) {
      item.id = parseInt(match[2], 10);
      item.label = match[1];
    }

    item.url = getItemUrl(item, filterItemUrls);
    result = item;
  }

  return result;
};

const getEditorPicksValueFromName = (item: IBaseItem): string => {
  const editorPicksNameValueMapping = {
    Vintage: 'vintage',
    'We love': 'welove'
  };
  return editorPicksNameValueMapping[item.label];
};

const checkPageParams = (
  item: IBaseItem,
  optionParams: ContentsRouteParameter.NameEnum[],
  pageParam: ContentsRouteParameter,
  oldResult: boolean
) => {
  optionParams.forEach(optionParam => {
    let itemValue: string;

    if (item.label && optionParam === ContentsRouteParameter.NameEnum.Tag) {
      itemValue = getEditorPicksValueFromName(item);
    } else {
      const parsedItem = parseItemLabelAndId(item.value);
      itemValue = String(parsedItem.id);
    }

    if (pageParam.name === optionParam && pageParam.value === itemValue) {
      oldResult = false;
    }
  });

  return oldResult;
};

/**
 * Check if we should filter facet items (hide some of them)
 *
 * @param optionParams
 * @param pageParams
 * @param item
 */
export const checkRouteParameters = (
  optionParams: ContentsRouteParameter.NameEnum[],
  pageParams: ContentsRouteParameter[],
  item: IBaseItem
): boolean => {
  let result = true;

  if (pageParams && optionParams) {
    pageParams.forEach(pageParam => {
      if (result) {
        result = checkPageParams(item, optionParams, pageParam, result);
      }
    });
  }

  return result;
};

export const convertNumberFromCents = function(numberInCents: number, operator: string): string {
  const NumValue = numberInCents / 100;
  return operator + ' ' + NumValue;
};

export const formatLabel = function(item: {
  computedLabel?: string;
  attributeName?: string;
  numericValue?: number;
  operator?: string;
}): string {
  let formatedLabel: string;
  const toggleFacetName = ToggleFacets.indexOf(item.attributeName) > -1 ? item.attributeName : null;

  if (item.attributeName.indexOf('price') !== -1) {
    formatedLabel = convertNumberFromCents(item.numericValue, item.operator);
  } else {
    const isHierarchical = item.attributeName.indexOf('lvl1') !== -1 || item.attributeName.indexOf('lvl2') !== -1;
    formatedLabel = parseItemLabelAndId(item.computedLabel, isHierarchical, toggleFacetName).label;
  }
  return formatedLabel;
};

/**
 * Sort filter items by their IDs
 *
 * @param itemA
 * @param itemB
 */
const sortById = (itemA, itemB) => {
  return itemA.name.split('#')[1] - itemB.name.split('#')[1];
};

/**
 * Sort filter items by numerical values of their names
 * @param itemA
 * @param itemB
 */
const sortByNumericValue = (itemA, itemB) => {
  return parseFloat(itemA.name.split('#')[0]) - parseFloat(itemB.name.split('#')[0]);
};

/**
 * Sort clothing filter (XS to XXXXXL)
 * @param itemA
 * @param itemB
 */
const sortClothingSizes = (itemA, itemB) => {
  const order = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', 'xxxxl', 'xxxxxl'];
  const parseItem: Function = item => {
    return item
      .toLowerCase()
      .replace('2xs', 'xxs')
      .replace('2xl', 'xxl')
      .replace('3xl', 'xxxl')
      .replace('4xl', 'xxxxl')
      .replace('5xl', 'xxxxxl');
  };

  return order.indexOf(parseItem(itemA.name.split('#')[0])) - order.indexOf(parseItem(itemB.name.split('#')[0]));
};

@Injectable()
export class FiltersService {
  private priceFilterState: Subject<PriceRangeState> = new Subject();
  private updatePriceFilterNeeded: Subject<void> = new Subject();

  constructor() {
    const priceRangeState = {} as PriceRangeState;
    this.setPriceFilterStatut(priceRangeState);
  }

  getPriceFilterUpdateNeeded(): Observable<void> {
    return this.updatePriceFilterNeeded.asObservable();
  }

  forcePriceFilterUpdate(): void {
    this.updatePriceFilterNeeded.next();
  }

  setPriceFilterStatut(priceRangeState: PriceRangeState) {
    this.priceFilterState.next(priceRangeState);
  }

  getPriceFilterStatut(): Observable<PriceRangeState> {
    return this.priceFilterState.asObservable();
  }

  /**
   * Returns the default list of filter.
   *
   * Filters order:
   *  1. Universe
   *  2. Category
   *  3. Brand
   *  4. Model
   *  5. Fast delivery
   *  6. Colour
   *  7. Price
   *  8. Size
   *  9. Condition
   *  10. Mecanism
   *  11. Material
   *  12. Editor's picks
   *  13. Shipped from
   *  14. Availability
   *  15. Discount
   */
  getDefaultFilters(localeService: LocaleService): CatalogFilter[] {
    const translationKey = localeService.getTranslationKey();
    const activeCurrency = localeService.getCurrency();
    const countryIso = localeService.getIsoCodeForSize(localeService.getCountry());

    const filtersList: CatalogFilter[] = [
      {
        type: 'checkbox-list',
        label: 'CATALOG.FILTERS.UNIVERSE',
        facet: `universe.facet.${translationKey}`,
        routeParameters: [ContentsRouteParameter.NameEnum.IdUniverse]
      },
      {
        type: 'checkbox-list',
        label: 'CATALOG.FILTERS.CATEGORY',
        facet: 'category.facet.' + translationKey + '.lvl0',
        requiredFacet: `universe.facet.${translationKey}`,
        routeParameters: [ContentsRouteParameter.NameEnum.IdCategory]
      },
      {
        type: 'checkbox-list',
        label: 'CATALOG.FILTERS.CATEGORY',
        facet: 'category.facet.' + translationKey + '.lvl1',
        requiredFacet: `category.facet.${translationKey}.lvl0`,
        isHierarchical: true,
        limit: 100,
        routeParameters: [ContentsRouteParameter.NameEnum.IdSubcategory]
      },
      {
        type: 'checkbox-list',
        label: 'CATALOG.FILTERS.BRANDS',
        facet: 'brand.facet',
        searchPlaceholder: 'CATALOG.FILTERS.SEARCH_PLACEHOLDER.BRANDS',
        isSearchable: true,
        limit: 100,
        routeParameters: [ContentsRouteParameter.NameEnum.IdBrand]
      },
      {
        type: 'checkbox-list',
        label: 'CATALOG.FILTERS.MODEL',
        facet: 'model.facet',
        requiredFacet: 'brand.facet',
        isSearchable: true,
        limit: 100,
        routeParameters: [ContentsRouteParameter.NameEnum.IdModel]
      },
      {
        type: 'locations-checkbox-list',
        label: 'CATALOG.FILTERS.LOCATION',
        facet: 'country',
        searchPlaceholder: 'CATALOG.FILTERS.SEARCH_PLACEHOLDER.COUNTRY',
        isSearchable: false,
        limit: 100
      },
      {
        type: 'toggle',
        label: 'DELIVERY_OPTS.EXPRESS',
        facet: 'stock'
      },
      {
        type: 'toggle',
        label: 'CATALOG.FILTERS.DS',
        facet: 'dsEligibleCountries'
      },
      {
        type: 'colors',
        label: 'CATALOG.FILTERS.COLORS',
        facet: `colors.facet.${translationKey}`,
        showMoreLimit: 100,
        limit: 100,
        sortBy: ['value:desc'],
        routeParameters: [ContentsRouteParameter.NameEnum.IdColors]
      },
      {
        type: 'price-range',
        label: 'CATALOG.FILTERS.PRICE.INPUT',
        facet: 'price.cents.' + activeCurrency,
        currency: activeCurrency
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.1',
        facet: `size.facet.type1.${countryIso}`,
        limit: 20,
        sortBy: sortClothingSizes
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.2',
        facet: `size.facet.type2.${countryIso}`,
        limit: 20,
        sortBy: sortByNumericValue
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.3',
        facet: `size.facet.type3.${countryIso}`,
        limit: 20,
        sortBy: sortByNumericValue
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.4',
        facet: `size.facet.type4.${countryIso}`,
        limit: 20,
        sortBy: sortByNumericValue
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.5',
        facet: `size.facet.type5.${countryIso}`,
        limit: 20,
        sortBy: sortByNumericValue
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.6',
        facet: `size.facet.type6.${countryIso}`,
        limit: 20,
        sortBy: sortByNumericValue
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.7',
        facet: `size.facet.type7.${countryIso}`,
        limit: 20,
        sortBy: sortById
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.8',
        facet: `size.facet.type8.${countryIso}`,
        limit: 20,
        sortBy: sortById
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.9',
        facet: `size.facet.type9.${countryIso}`,
        limit: 20,
        sortBy: sortById
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.10',
        facet: `size.facet.type10.${countryIso}`,
        limit: 20,
        sortBy: sortClothingSizes
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.11',
        facet: `size.facet.type11.${countryIso}`,
        limit: 20,
        sortBy: sortById
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.12',
        facet: `size.facet.type12.${countryIso}`,
        limit: 10,
        sortBy: sortById
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.13',
        facet: `size.facet.type13.${countryIso}`,
        limit: 20,
        sortBy: sortClothingSizes
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.14',
        facet: `size.facet.type14.${countryIso}`,
        limit: 20,
        sortBy: sortById
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.15',
        facet: `size.facet.type15.${countryIso}`,
        limit: 20,
        sortBy: sortByNumericValue
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.16',
        facet: `size.facet.type16.${countryIso}`,
        limit: 20,
        sortBy: sortByNumericValue
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.17',
        facet: `size.facet.type17.${countryIso}`,
        limit: 20,
        sortBy: sortById
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.18',
        facet: `size.facet.type18.${countryIso}`,
        limit: 20,
        sortBy: sortByNumericValue
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.19',
        facet: `size.facet.type19.${countryIso}`,
        limit: 20,
        sortBy: sortByNumericValue
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.20',
        facet: `size.facet.type20.${countryIso}`,
        limit: 20,
        sortBy: sortByNumericValue
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.21',
        facet: `size.facet.type21.${countryIso}`,
        limit: 10,
        sortBy: sortClothingSizes
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.22',
        facet: `size.facet.type22.${countryIso}`,
        limit: 20,
        sortBy: sortById
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.23',
        facet: `size.facet.type23.${countryIso}`,
        limit: 10,
        sortBy: sortById
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.24',
        facet: `size.facet.type24.${countryIso}`,
        limit: 20,
        sortBy: sortById
      },
      {
        type: 'short-checkbox-list',
        label: 'CATALOG.FILTERS.SIZE.25',
        facet: `size.facet.type25.${countryIso}`,
        limit: 20,
        sortBy: sortById
      },
      {
        type: 'checkbox-list',
        label: 'CATALOG.FILTERS.CONDITION',
        facet: `condition.facet.${translationKey}`,
        sortBy: sortById
      },
      {
        type: 'checkbox-list',
        label: 'CATALOG.FILTERS.WATCH_MECHANISM',
        facet: `watchMechanism.facet.${translationKey}`
      },
      {
        type: 'checkbox-list',
        label: 'CATALOG.FILTERS.MATERIALS',
        facet: `materials.facet.${translationKey}.lvl0`,
        showMoreLimit: 100,
        limit: 10,
        routeParameters: [ContentsRouteParameter.NameEnum.IdMaterials]
      },
      {
        type: 'checkbox-list',
        label: 'CATALOG.FILTERS.MATERIALS',
        facet: `materials.facet.${translationKey}.lvl1`,
        requiredFacet: `materials.facet.${translationKey}.lvl0`,
        isHierarchical: true,
        showMoreLimit: 100,
        limit: 10,
        routeParameters: [ContentsRouteParameter.NameEnum.IdMaterials]
      },
      {
        type: 'checkbox-list',
        label: 'CATALOG.FILTERS.EDITORPICKS',
        facet: `editorPicks.facet.${translationKey}`,
        routeParameters: [ContentsRouteParameter.NameEnum.Tag]
      },
      { type: 'checkbox-list', label: 'CATALOG.FILTERS.SOLD', facet: 'sold.sold' },
      { type: 'checkbox-list', label: 'CATALOG.FILTERS.DISCOUNT', facet: 'discount.ranges' }
    ];

    return filtersList;
  }
}
